import { deepCloneObject } from './common'

/**
 * Merge a base page with one of its page versions, mirroring the merge semantics used
 * by cms-template-default's prepareStoreData.
 *
 * Precedence:
 * - top-level `setting` (parts, gallery, variants, fallback_img): the page VERSION wins,
 *   because a version exists to override the served content.
 * - `setting.settings` (configurations, shipping, social_proof, colors, traffic_config):
 *   the base PAGE wins, so a version inherits the page's configuration.
 * - `setting.settings.general`: the version wins, except `id` which is pinned to the base
 *   page id so the served page keeps its canonical identity.
 * - `setting.settings.analytics`: always the base page's analytics.
 */
export const mergePageVersion = (page: S3Types.page, pageVersion?: S3Types.page): S3Types.page => {
  if (!pageVersion) return page
  return {
    ...page,
    ...pageVersion,
    setting: {
      ...(page?.setting || {}),
      ...(pageVersion?.setting || {}),
      settings: {
        ...(pageVersion?.setting?.settings || {}),
        ...(page?.setting?.settings || {}),
        analytics: page?.setting?.settings?.analytics,
        general: {
          ...(page?.setting?.settings?.general || {}),
          ...(pageVersion?.setting?.settings?.general || {}),
          id: page?.setting?.settings?.general?.id
        }
      }
    }
  } as S3Types.page
}

/**
 * Weighted random pick over `candidates` using `weights` (page version id → weight).
 * Cumulative-distribution selection: a random point in [0, totalWeight) falls into
 * the band of exactly one candidate. Falls back to the first/last candidate when the
 * weights are all zero or a rounding gap leaves the point past the last band.
 * Ported from cms-template-default's pickAmongCandidates.
 */
const pickAmongCandidates = (weights: { [key: string]: number }, candidates: string[]): string => {
  const totalWeight = candidates.reduce((sum, vid) => sum + (weights[vid] || 0), 0)
  if (!totalWeight) return candidates[0]

  const randomPoint = Math.random() * totalWeight
  let cumulative = 0
  return (
    candidates.find((vid) => {
      cumulative += weights[vid] || 0
      return randomPoint < cumulative
    }) || candidates[candidates.length - 1]
  )
}

/**
 * Self-correcting pick: among candidates already seen for this pageId, return the one
 * furthest below its target share so repeated picks converge on the configured split
 * rather than drifting on every request's own randomness.
 * Ported from cms-template-default's getUnderservedVersion.
 */
const getUnderservedVersion = (
  candidates: string[],
  pageId: string,
  weights: { [key: string]: number }
): string => {
  const served = process.cachePageVersion?.[pageId] || {}
  const sumServed = Object.values(served).reduce((sum, n) => sum + n, 0) || 1
  const totalWeight = candidates.reduce((sum, vid) => sum + (weights[vid] || 0), 0) || 1
  return (
    candidates.find((vid) => {
      const targetRatio = (weights[vid] || 0) / totalWeight
      return targetRatio >= (served[vid] ?? 0) / sumServed
    }) || ''
  )
}

/**
 * Pick a page version id for `pageId` from `candidates`, weighted by `weights`
 * (`traffic_config`), converging to the configured split via process.cachePageVersion.
 * A 0% version is excluded entirely (never served, not merely rare). Ported from
 * cms-template-default's pickWeightedVersion.
 */
const pickWeightedVersion = (
  pageId: string,
  candidates: string[],
  weights: { [key: string]: number }
): string => {
  if (candidates.length <= 1) return candidates[0] || ''

  const eligible = candidates.filter((vid) => (weights[vid] || 0) > 0)
  if (!eligible.length) return candidates[0]
  if (eligible.length === 1) return eligible[0]

  const cache = process.cachePageVersion?.[pageId]
  // Require the SAME key set both ways: a cache built from a prior candidate set (a
  // version added/removed/re-weighted since) must not be reused — that would let
  // getUnderservedVersion pick a version absent from cache and increment undefined→NaN.
  const cacheMatchesEligible =
    !!cache &&
    Object.keys(cache).length === eligible.length &&
    eligible.every((vid) => vid in cache)

  if (cacheMatchesEligible && process.cachePageVersion) {
    const shouldVersion = getUnderservedVersion(eligible, pageId, weights)
    if (shouldVersion) {
      process.cachePageVersion[pageId][shouldVersion]++
      return shouldVersion
    }
  }

  const chooseVersion = pickAmongCandidates(weights, eligible)
  process.cachePageVersion = process.cachePageVersion || {}
  process.cachePageVersion[pageId] = eligible.reduce(
    (acc, vid) => ({ ...acc, [vid]: vid === chooseVersion ? 1 : 0 }),
    {} as { [key: string]: number }
  )
  return chooseVersion
}

/**
 * Resolve which page version id to SERVE for a base page by splitting its traffic across
 * `traffic_config`. This is the write-side resolver the redirect middleware uses to pick
 * a version once and pin it to the `pversion_<slug>` cookie; the render side then reads
 * that cookie via `resolvePageVersionId` so a visitor stays on one version (sticky).
 *
 * Order: weighted split over `traffic_config` (self-correcting) → `priority_version_id`
 * → first `traffic_config` key → ''. Returns '' when the page has no versions.
 */
export const resolvePageVersionTraffic = (page?: S3Types.page): string => {
  const weights = page?.setting?.settings?.traffic_config || {}
  const candidates = Object.keys(weights)
  const pageId = page?.setting?.settings?.general?.id || ''
  const picked = candidates.length ? pickWeightedVersion(pageId, candidates, weights) : ''
  return `${picked || page?.setting?.settings?.general?.priority_version_id || candidates[0] || ''}`
}

/**
 * Resolve which page version id should be served for a base page.
 * Order: per-slug preview cookie `pversion_<slug>` → page's `priority_version_id`
 * → first key of `traffic_config`. Returns '' when the page has no versions.
 */
export const resolvePageVersionId = (params: {
  page?: S3Types.page
  slug?: string
  pVersionCookies?: { [slug: string]: string }
}): string => {
  const { page, slug, pVersionCookies } = params
  const cookieVersion = slug ? pVersionCookies?.[slug] : undefined
  return `${
    cookieVersion ||
    page?.setting?.settings?.general?.priority_version_id ||
    Object.keys(page?.setting?.settings?.traffic_config || {})?.[0] ||
    ''
  }`
}

/**
 * Resolve the served version for `page` and return the merged page. Backward compatible:
 * a page with no resolvable version, or a version id absent from the repository, returns
 * the base page unchanged. The looked-up version is deep-cloned before merging so the
 * long-lived `process._repository.page_versions` copy is never mutated downstream.
 */
export const applyPageVersion = (
  page: S3Types.page,
  params: {
    slug?: string
    pVersionCookies?: { [slug: string]: string }
  }
): S3Types.page => {
  const pageVersionId = resolvePageVersionId({ page, ...params })
  if (!pageVersionId) return page
  const pageVersion = process._repository.page_versions?.[pageVersionId]
  if (!pageVersion) return page
  return mergePageVersion(page, deepCloneObject(pageVersion) as S3Types.page)
}

/**
 * Parse `pversion_<slug>` entries out of an already-parsed cookie map into a
 * `{ [slug]: pageVersionId }` lookup for resolvePageVersionId / applyPageVersion.
 */
export const parsePVersionCookies = (cookies?: {
  [key: string]: string
}): { [slug: string]: string } => {
  const result: { [slug: string]: string } = {}
  if (!cookies) return result
  Object.entries(cookies).forEach(([key, value]) => {
    if (key.startsWith('pversion_') && value) {
      result[key.slice('pversion_'.length)] = value
    }
  })
  return result
}
